import React, { useEffect, useRef, useState } from 'react'
import { store } from '../../../app/store';
import { postAPI, formatDate, convertTo12Hour } from '../../../app/api';
import './tooltip.css';
import { hasPermission, isAdmin } from '../../../store/hooks/Permission';
import Excel from '../../../components/Excel/Excel';
import { DownloadOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import Loader from '../../../UIComponents/Loader';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import UserInfo from '../../Users/Components/UserInfo';

export default function List() {
    const { exam_id } = useParams();
    const store_data = JSON.parse(store());
    const [Enrollments, setEnrollments] = useState([]);
    const [Loading, setLoading] = useState(true);
    const [ShowProfile, setShowProfile] = useState(false);

    // Permission states
    const [canView, setCanView] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [canExport, setCanExport] = useState(false);

    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedUserOpenKey, setSelectedUserOpenKey] = useState(0);
    const userInfoModalTriggerRef = useRef(null);
    
    const fetchEnrollments = async () => {
        const filter = { org_id: store_data?.org_id, exam_id: exam_id }
        const response = await postAPI("/exam-enrollments/list", filter);
        if (response?.status) {
            setEnrollments(response?.data)
            setLoading(false)
        } else {
            setEnrollments([])
            setLoading(false)
        }
    }

    useEffect(() => {
        const checkPermissions = async () => {
            if (isAdmin()) {
                setCanView(true);
                setCanEdit(true);
                setCanDelete(true);
                setCanExport(true);
                fetchEnrollments();
            } else {
                const viewAllowed = await hasPermission("EXAM", "Enrollments", "VIEW");
                const editAllowed = await hasPermission("EXAM", "Enrollments", "EDIT");
                const deleteAllowed = await hasPermission("EXAM", "Enrollments", "DELETE");
                const exportAllowed = await hasPermission("EXAM", "Enrollments", "EXPORT");

                setCanView(viewAllowed);
                setCanEdit(editAllowed);
                setCanDelete(deleteAllowed);
                setCanExport(exportAllowed);

                if (viewAllowed) {
                    fetchEnrollments();
                } else {
                    setLoading(false)
                    toast.error('You do not have permission to view this page.');
                }
            }
        };
        checkPermissions();
        // eslint-disable-next-line
    }, [])

    const getPaymentStatusBadge = (val) => {
        if (parseFloat(val?.enrollment_fee) === 0) {
            return <span className="badge bg-label-info me-1">Free</span>;
        }
        if (val?.is_paid == 1 && val?.is_payment_verified == 1) {
            return <span className="badge bg-label-success me-1">Paid &amp; Verified</span>;
        }
        if (val?.is_paid == 1) {
            return <span className="badge bg-label-warning me-1">Paid (Pending Verify)</span>;
        }
        return <span className="badge bg-label-danger me-1">Unpaid</span>;
    }

    const handleOpenUserInfo = (participantId) => {
        setSelectedUser(participantId);
        setSelectedUserOpenKey(prev => prev + 1);
    };

    useEffect(() => {
        if (selectedUser && userInfoModalTriggerRef.current) {
            userInfoModalTriggerRef.current.click();
        }
    }, [selectedUser, selectedUserOpenKey]);

    if (Loading) return <Loader />;

    const showProfileModal = () => {
        setShowProfile(true);
    }

    const hideProfileModal = () => {
        setShowProfile(false);
    }

    return (
        <>
            <ToastContainer />
            <div className="container-fluid mb-3 gap-2 p-0 d-flex justify-content-between align-items-center">
                <div>
                    {Enrollments?.length > 0 && (
                        <span className="badge bg-label-primary">
                            {Enrollments[0]?.exam_title} &mdash; {Enrollments.length} Enrollment{Enrollments.length !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>
                <button className="btn btn-primary btn-sm" disabled={!canExport}>
                    <Excel
                        data={{ Name: "Enrollments", Data: Enrollments }}
                        filename="enrollments-export.xlsx"
                    >
                        <DownloadOutlined /> Export
                    </Excel>
                </button>
            </div>
            <table className="table card-table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Participant</th>
                        <th>Enrollment Date</th>
                        <th>Enrollment Fee</th>
                        <th>Discount / Coupon</th>
                        <th>Final Payable</th>
                        <th>Payment Status</th>
                        <th>Transaction ID</th>
                    </tr>
                </thead>
                <tbody className="table-border-bottom-0">
                    {!canView ? (
                        <tr>
                            <td colSpan="8" className="text-center text-danger py-4">
                                You do not have permission to view this page.
                            </td>
                        </tr>
                    ) : Enrollments && Enrollments?.length > 0 ?
                        Enrollments?.map((val, index) => (
                            <tr key={val?.id || index}>
                                <td>{index + 1}</td>
                                <td type="button" data-bs-toggle="modal" data-bs-target={`#UserInfoEnrollment`} onClick={() => setSelectedUser(val?.participant_id)}>
                                    <p className="mb-0 fw-semibold">{val?.participant_name}</p>

                                    <small className="text-muted d-block">{val?.participant_email}</small>
                                    <small className="text-muted d-block">{val?.participant_phone}</small>
                                </td>
                                <td>
                                    {val?.enrollment_date ? (
                                        <>
                                            <p className="mb-0">{formatDate(val?.enrollment_date?.split(' ')[0])?.month} {formatDate(val?.enrollment_date?.split(' ')[0])?.day}, {formatDate(val?.enrollment_date?.split(' ')[0])?.year}</p>
                                            <small className="text-muted">{val?.enrollment_date?.split(' ')[1]}</small>
                                        </>
                                    ) : '—'}
                                </td>
                                <td>₹{parseFloat(val?.enrollment_fee || 0).toFixed(2)}</td>
                                <td>
                                    {val?.is_coupon_applied == 1 ? (
                                        <>
                                            <span className="badge bg-label-success me-1">{val?.coupon_code}</span>
                                            <small className="text-muted d-block">-₹{parseFloat(val?.discount_amount || 0).toFixed(2)}</small>
                                        </>
                                    ) : (
                                        <span className="text-muted">—</span>
                                    )}
                                </td>
                                <td>
                                    <span className="fw-semibold">₹{parseFloat(val?.final_payable_amount || 0).toFixed(2)}</span>
                                    {parseFloat(val?.wallet_settlement_amount) > 0 && (
                                        <small className="text-muted d-block">Wallet: ₹{parseFloat(val?.wallet_settlement_amount).toFixed(2)}</small>
                                    )}
                                </td>
                                <td>{getPaymentStatusBadge(val)}</td>
                                <td>
                                    {val?.transaction_id && val?.transaction_id != 0 ? (
                                        <small className="text-muted">{val?.transaction_id}</small>
                                    ) : (
                                        <span className="text-muted">—</span>
                                    )}
                                </td>
                            </tr>
                        ))
                        : (
                            <tr>
                                <td colSpan="8" className="text-center text-muted py-4">
                                    No enrollments found for this exam.
                                </td>
                            </tr>
                        )
                    }
                </tbody>
            </table>
            <button
                ref={userInfoModalTriggerRef}
                type="button"
                className="d-none"
                data-bs-toggle="modal"
                data-bs-target="#UserInfoEnrollment"
                tabIndex={-1}
                aria-hidden="true"
            />
            <UserInfo PopupID="UserInfoEnrollment" userId={selectedUser} openKey={selectedUserOpenKey} />
        </>
    )
}
